import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Loader2, Maximize2, FileText, Lock } from "lucide-react";

export const NdaAgreement = ({ accepted, onAcceptedChange }) => {
  const [nda, setNda] = useState(null);
  const [readComplete, setReadComplete] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const boxRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    api.get("/nda").then(({ data }) => setNda(data)).catch(() => setNda({ text: "", version: "", pdf_url: "" }));
  }, []);

  const checkScroll = (el) => {
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 24) setReadComplete(true);
  };

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3" data-testid="nda-section">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-sm font-semibold">
          <FileText className="h-4 w-4 text-primary" />
          Beta Participant NDA
        </div>
        <button type="button" onClick={() => setModalOpen(true)} className="flex items-center gap-1 text-xs text-primary font-semibold hover:underline" data-testid="nda-fullscreen-button">
          <Maximize2 className="h-3 w-3" /> View full screen
        </button>
      </div>

      {!nda ? (
        <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-primary" /></div>
      ) : (
        <div
          ref={boxRef}
          onScroll={(e) => checkScroll(e.target)}
          className="h-40 overflow-y-auto rounded-md border border-border bg-white px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap text-muted-foreground"
          data-testid="nda-scroll-box"
        >
          {nda.text}
        </div>
      )}

      {!readComplete && (
        <p className="mt-2 text-[11px] text-muted-foreground flex items-center gap-1" data-testid="nda-scroll-hint">
          <Lock className="h-3 w-3" /> Scroll to the bottom of the NDA to enable agreement
        </p>
      )}

      <div className="mt-3 flex items-start gap-2">
        <Checkbox
          id="nda-agree"
          checked={accepted}
          disabled={!readComplete}
          onCheckedChange={(v) => onAcceptedChange(!!v)}
          data-testid="nda-agree-checkbox"
          className="mt-0.5"
        />
        <Label htmlFor="nda-agree" className={`text-xs leading-snug ${!readComplete ? "opacity-50" : ""}`}>
          I have read and agree to the OHM Exp Beta Participation Agreement (NDA), including its confidentiality obligations.
        </Label>
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl h-[85vh] flex flex-col" data-testid="nda-modal">
          <DialogHeader>
            <DialogTitle className="font-display">Beta Participation Agreement (NDA)</DialogTitle>
            {nda?.version && <p className="text-xs text-muted-foreground">{nda.version}</p>}
          </DialogHeader>
          <div
            ref={modalRef}
            onScroll={(e) => checkScroll(e.target)}
            className="flex-1 overflow-y-auto rounded-md border border-border bg-muted/20 px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap"
            data-testid="nda-modal-text"
          >
            {nda?.text || ""}
          </div>
          {nda?.pdf_url && (
            <a href={nda.pdf_url} target="_blank" rel="noreferrer" className="text-xs text-primary font-semibold hover:underline" data-testid="nda-pdf-link">
              Download original PDF
            </a>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
